'use client';

/**
 * RoleTilesGrid — V3 dark, "who actually uses it" persona-tile grid.
 *
 * Shared across feature pages that answer the "is this for me?" beat with
 * a flat grid of role-shaped tiles (icon → name → one-liner → metric chip).
 * Used today by /business-cards (ResultSection) and /e-signature
 * (ESignatureExamples). The card chrome, typography, and metric chip are
 * fixed; the caller passes the section copy, the role list, and an optional
 * footnote (e.g., stats provenance).
 */

import React from 'react';
import { IconType } from 'react-icons';
import SectionLabel from '../../home/SectionLabel';

export type RoleTileItem = {
  key: string;
  Icon: IconType;
  name: string;
  line: string;
  metric: string;
  metricLabel: string;
};

type Props = {
  num: string;
  label: string;
  headlineA: string;
  headlineB: string;
  subhead: string;
  roles: RoleTileItem[];
  footnote?: string;
  borderTop?: string;
  minCardWidth?: number;
};

const RoleTilesGrid = ({
  num,
  label,
  headlineA,
  headlineB,
  subhead,
  roles,
  footnote,
  borderTop = '1px solid rgba(255,255,255,0.05)',
  minCardWidth = 280,
}: Props) => {
  return (
    <section
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0A0E27',
        color: '#F5F7FA',
        ...(borderTop ? { borderTop } : {}),
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num={num} label={label} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {headlineA} <span style={{ color: '#B4C1D8' }}>{headlineB}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 48px',
          }}
        >
          {subhead}
        </p>

        <div className="grid gap-5" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${minCardWidth}px, 1fr))` }}>
          {roles.map(({ key, Icon, name, line, metric, metricLabel }) => (
            <div
              key={key}
              className="rounded-2xl p-7 transition-all duration-200 flex flex-col"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <div
                className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-5"
                style={{
                  background: 'rgba(34,211,238,0.12)',
                  border: '1px solid rgba(34,211,238,0.3)',
                }}
              >
                <Icon className="w-6 h-6" style={{ color: '#22D3EE' }} />
              </div>
              <h3
                className="font-semibold"
                style={{
                  fontSize: 19,
                  letterSpacing: '-0.01em',
                  color: '#F5F7FA',
                  margin: '0 0 8px',
                }}
              >
                {name}
              </h3>
              <p
                style={{
                  color: '#B4C1D8',
                  fontSize: 14,
                  lineHeight: 1.55,
                  margin: '0 0 16px',
                }}
              >
                {line}
              </p>
              <div
                className="inline-flex items-baseline gap-2 self-start mt-auto"
                style={{
                  background: 'rgba(34,211,238,0.08)',
                  border: '1px solid rgba(34,211,238,0.2)',
                  borderRadius: 8,
                  padding: '6px 10px',
                }}
              >
                <span
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: '#22D3EE',
                    letterSpacing: '-0.01em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {metric}
                </span>
                <span style={{ fontSize: 12, color: '#B4C1D8', whiteSpace: 'nowrap' }}>{metricLabel}</span>
              </div>
            </div>
          ))}
        </div>

        {footnote ? (
          <p
            style={{
              color: '#6B7593',
              fontSize: 12,
              margin: '32px 0 0',
              maxWidth: 720,
              lineHeight: 1.5,
            }}
          >
            {footnote}
          </p>
        ) : null}
      </div>
    </section>
  );
};

export default RoleTilesGrid;
